/**
 * CopyButton — token-driven "copy to clipboard" affordance.
 *
 * Wraps `navigator.clipboard.writeText` with a small visual state
 * machine so consumers do not have to reimplement the "copied!"
 * acknowledgement on every demo. The button cycles through three
 * states — `idle` → `copied` → `idle` — and fires an `onCopy` callback
 * whenever the clipboard write succeeds so host applications can wire
 * up their own toast / analytics.
 *
 * Single responsibility: copy a string and acknowledge it. The button
 * surface itself stays neutral; consumers compose it inside their own
 * code, link, or panel chrome.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { GRAY, PRIMARY, SAFE } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export type CopyButtonState = "idle" | "copied" | "error";

export interface CopyButtonProps {
  /** Text written to the clipboard when the button is activated. */
  readonly value: string;
  /** Label shown in the `idle` state. Defaults to "Copy". */
  readonly idleLabel?: string;
  /** Label shown in the `copied` state. Defaults to "Copied". */
  readonly copiedLabel?: string;
  /** Label shown in the `error` state. Defaults to "Failed". */
  readonly errorLabel?: string;
  /** How long (ms) the `copied` / `error` state lingers before reset. Defaults to 1500. */
  readonly resetMs?: number;
  /** Invoked after the clipboard write resolves successfully. */
  readonly onCopy?: (value: string) => void;
  /** Additional className passed to the underlying button. */
  readonly className?: string;
}

function tone(state: CopyButtonState): { color: string; background: string; border: string } {
  if (state === "copied") {
    return { color: SAFE[700], background: SAFE[100], border: SAFE[300] };
  }
  if (state === "error") {
    return { color: GRAY[900], background: GRAY[100], border: GRAY[400] };
  }
  return { color: PRIMARY[700], background: PRIMARY[100], border: PRIMARY[300] };
}

function labelFor(state: CopyButtonState, idle: string, copied: string, error: string): string {
  if (state === "copied") return copied;
  if (state === "error") return error;
  return idle;
}

function IconForState({ state }: { readonly state: CopyButtonState }): React.JSX.Element {
  if (state === "copied") return <Check aria-hidden="true" size={13} />;
  if (state === "error") return <X aria-hidden="true" size={13} />;
  return <Copy aria-hidden="true" size={13} />;
}

/**
 * Renders a button that copies `value` to the clipboard and reflects
 * success / failure for a brief acknowledgement window.
 */
export const CopyButton = React.memo(function CopyButton({
  value,
  idleLabel = "Copy",
  copiedLabel = "Copied",
  errorLabel = "Failed",
  resetMs = 1500,
  onCopy,
  className,
}: CopyButtonProps): React.JSX.Element {
  const [state, setState] = useState<CopyButtonState>("idle");
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return (): void => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const scheduleReset = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setState("idle");
      timerRef.current = null;
    }, resetMs);
  }, [resetMs]);

  const handleClick = useCallback(async () => {
    try {
      const clipboard = navigator.clipboard as Clipboard | undefined;
      if (clipboard === undefined) {
        throw new Error("Clipboard API unavailable");
      }
      await clipboard.writeText(value);
      setState("copied");
      onCopy?.(value);
    } catch {
      setState("error");
    }
    scheduleReset();
  }, [onCopy, scheduleReset, value]);

  const { color, background, border } = tone(state);
  const label = labelFor(state, idleLabel, copiedLabel, errorLabel);

  return (
    <button
      type="button"
      onClick={() => {
        void handleClick();
      }}
      aria-live="polite"
      data-csk-copy-state={state}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium",
        "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        className,
      )}
      style={{
        alignItems: "center",
        backgroundColor: background,
        border: `1px solid ${border}`,
        borderRadius: 6,
        color,
        cursor: "pointer",
        display: "inline-flex",
        fontSize: "0.75rem",
        fontWeight: 700,
        gap: 4,
        minHeight: 28,
        padding: "0.3rem 0.55rem",
      }}
    >
      <IconForState state={state} />
      <span>{label}</span>
    </button>
  );
});
